import { encounterField } from '../shared/array-types';
import { displayEncounterZone, locationDisplayText, renderEncounterDisplayLinks, gmNotesVisible, type LinksInput, type EncounterDisplayInput } from '../janus-engine/port/k5-render/encounter-display';
import { htmlspecialchars as h } from '../janus-engine/port/k5-render/support';
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';

function iconInfo(id: unknown): string {
    return id ? `<span role="button" data-ft-encounter-info="${h(id)}">${iconMap(String(id))}</span>` : '';
}
function renderEncounterLinks(input: LinksInput): string {
    return renderEncounterDisplayLinks(input, {
        icon: name => iconMap(name), info: iconInfo,
        thumbnail: data => {
            const bridge = (window as unknown as { FtThumbnail?: { build: (row: unknown) => HTMLElement } }).FtThumbnail;
            if (!bridge) throw new Error('thumbnail-browser-renderer-missing');
            return bridge.build(data).outerHTML;
        },
    });
}

export function mountEncounterDisplays(root: ParentNode = document): void {
    const nodes = <T extends Element>(selector: string): T[] => [
        ...(root instanceof Element && root.matches(selector) ? [root as T] : []),
        ...Array.from(root.querySelectorAll<T>(selector)),
    ];
    for (const mount of nodes<HTMLElement>('[data-ft-encounter-display]')) {
        if (mount.dataset.ftEncounterDisplayMounted) continue;
        const script = mount.querySelector<HTMLScriptElement>('script[type="application/json"]');
        const output = mount.querySelector<HTMLElement>('[data-ft-encounter-display-output]');
        if (!script || !output) continue;
        mount.dataset.ftEncounterDisplayMounted = '1';
        try {
            const seed = JSON.parse(script.textContent || '') as EncounterDisplayInput;
            const id = String(encounterField(seed, 'encounter_id') ?? '');
            const held = id ? window.FateTide?.encounterCache?.get(id) : undefined;
            const input = held && typeof held === 'object' && String(encounterField(held, 'encounter_id') ?? '').toUpperCase() === id.toUpperCase()
                && mount.dataset.ftEncounterDisplay !== 'links' ? held as EncounterDisplayInput : seed;
            const mode = mount.dataset.ftEncounterDisplay;
            if (mode === 'gm-notes') {
                const notes = seed as unknown as { can_manage: boolean; notes: string };
                mount.hidden = !gmNotesVisible(notes);
                if (!mount.hidden) {
                    const payload = JSON.stringify({ text: notes.notes }).replace(/</g, '\\u003c');
                    output.innerHTML = `<div class="ft-lore-panel ft-lore-panel--gm mb-3"><div class="ft-lore-kicker">${iconMap('gm_notes', 16, 16, false)} GM Notes</div><span class="ft-pt-mount"><script type="application/json" class="ft-pt-blob">${payload}</script></span></div>`;
                    const parse = (window as unknown as { ftMountParsedText?: (root: Element) => void }).ftMountParsedText;
                    if (!parse) throw new Error('parse-text-browser-renderer-missing');
                    parse(output);
                }
            } else if (mode?.startsWith('location:')) output.textContent = locationDisplayText(input, mode.slice(9));
            else if (mode === 'links') output.innerHTML = renderEncounterLinks(input as LinksInput);
            else if (mode === 'conditions') {
                output.innerHTML = ['encounter_environment', 'encounter_weather', 'encounter_light'].map(field => {
                    const id = encounterField<string>(input, field);
                    const name = id ? seed.names?.[id] || (window as unknown as { ftItemNameById?: (id: string) => string }).ftItemNameById?.(id) : '';
                    return id ? `<span class="ft-env-chip">${iconInfo(id)} ${h(name || id)}</span>` : '';
                }).join('');
            } else {
                const zone = displayEncounterZone(input), name = decodeHtmlEntitiesForPlainText(encounterField(input, 'encounter_name'));
                output.textContent = mode === 'zone-name' ? [zone, name].filter(Boolean).join(' | ')
                    : mode === 'zone-prefix' ? (zone ? `${zone} — ` : '') : zone;
                if (mode === 'zone-badge') {
                    mount.hidden = !zone;
                    output.className = zone ? 'badge text-bg-secondary' : '';
                }
            }
        } catch (error) {
            output.textContent = `Encounter display unavailable: ${error instanceof Error ? error.message : 'invalid-input'}`;
            output.classList.add('text-warning');
        }
    }
    for (const option of nodes<HTMLOptionElement>('option[data-ft-encounter-zone-option]')) {
        if (option.dataset.ftEncounterDisplayMounted) continue;
        option.dataset.ftEncounterDisplayMounted = '1';
        try {
            const input = JSON.parse(option.dataset.ftEncounterZoneOption || '') as EncounterDisplayInput;
            option.textContent = [displayEncounterZone(input), decodeHtmlEntitiesForPlainText(encounterField(input, 'encounter_name'))].filter(Boolean).join(' | ');
            const select = option.closest('select') as (HTMLSelectElement & { tomselect?: { updateOption: (value: string, data: unknown) => void } }) | null;
            select?.tomselect?.updateOption(option.value, { value: option.value, text: option.textContent });
        } catch { option.textContent = 'Encounter label unavailable: invalid-input'; }
    }
    for (const holder of nodes<HTMLElement>('[data-ft-encounter-info]')) {
        if (!holder.childNodes.length) holder.innerHTML = iconMap(holder.dataset.ftEncounterInfo || '');
    }
}

document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-ft-encounter-info]') : null;
    if (target?.dataset.ftEncounterInfo) openItemInfo(target.dataset.ftEncounterInfo);
});
const scan = (): void => mountEncounterDisplays();
Object.assign(window, { ftMountEncounterDisplays: mountEncounterDisplays });
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true }); else scan();
